import { createSlice } from '@reduxjs/toolkit';
import {
  createProduct,
  fetchCatalog,
  fetchProductOptions,
  updateProduct,
  uploadCatalog,
} from './catalogThunks';
import { ALL_CLIENTS } from './catalogConstants';

const initialState = {
  /** Current page of the catalog. */
  products: [],
  totalCount: 0,
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchCatalog.fulfilled`. */
  requestId: null,
  filters: {
    search: '',
    company: ALL_CLIENTS,
    /** On by default: what was taken out of service is history, not catalog. */
    onlyActive: true,
    /** Only what has a balance: in the warehouse in use, or anywhere when none is picked. */
    onlyWithStock: false,
    page: 1,
    rowsPerPage: 20,
  },

  /** The form is being saved. Its failure is shown inside the form, not here. */
  saving: false,
  /** The row being taken out of service or brought back. */
  busyId: null,
  uploading: false,

  /** Every active product of `optionsCompanyId`, for pickers rather than the table. */
  options: [],
  optionsCompanyId: null,
  loadingOptions: false,
};

const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    /** Merges a patch into the filters; anything but a page change goes back to page 1. */
    setCatalogFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalog.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchCatalog.fulfilled, (state, action) => {
        // Only the latest request may write: a save reloads the listing while
        // a filter may still be on its way.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.products = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
      })
      .addCase(fetchCatalog.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
        state.products = [];
        state.totalCount = 0;
      })

      .addCase(fetchProductOptions.pending, (state, action) => {
        state.loadingOptions = true;
        // Products of another company must not be pickable while these load.
        if (state.optionsCompanyId !== action.meta.arg) state.options = [];
        state.optionsCompanyId = action.meta.arg;
      })
      .addCase(fetchProductOptions.fulfilled, (state, action) => {
        if (action.payload.companyId !== state.optionsCompanyId) return;
        state.loadingOptions = false;
        state.options = action.payload.products;
      })
      .addCase(fetchProductOptions.rejected, (state, action) => {
        if (action.meta.arg !== state.optionsCompanyId) return;
        state.loadingOptions = false;
        state.options = [];
      })

      .addCase(createProduct.pending, (state) => {
        state.saving = true;
      })
      .addCase(createProduct.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createProduct.rejected, (state) => {
        state.saving = false;
      })

      // An edit comes from the form; a switch of `is_active` comes from the
      // row. Both are the same call, so both flags follow it.
      .addCase(updateProduct.pending, (state, action) => {
        state.saving = true;
        state.busyId = action.meta.arg.productId;
      })
      .addCase(updateProduct.fulfilled, (state) => {
        state.saving = false;
        state.busyId = null;
      })
      .addCase(updateProduct.rejected, (state) => {
        state.saving = false;
        state.busyId = null;
      })

      .addCase(uploadCatalog.pending, (state) => {
        state.uploading = true;
      })
      .addCase(uploadCatalog.fulfilled, (state) => {
        state.uploading = false;
        // The sheet may have added or changed what a picker was offering.
        state.optionsCompanyId = null;
        state.options = [];
      })
      .addCase(uploadCatalog.rejected, (state) => {
        state.uploading = false;
      });
  },
});

export const { setCatalogFilters } = catalogSlice.actions;

export default catalogSlice.reducer;
